<script lang="tsx">
  import { Col, Form, Tooltip } from 'ant-design-vue';
  import { upperFirst } from 'lodash-es';
  import { computed, defineComponent, Fragment, toRefs, unref } from 'vue';
  import type { Nullable, Recordable } from '@mts-fe/types';
  import type { Rule as ValidationRule } from 'ant-design-vue/es/form/interface';
  import type { PropType, Ref } from 'vue';
  import {
    cloneDeep,
    DAY_FORMAT,
    getSlot,
    isArray,
    isBoolean,
    isFunction,
    isNil,
    isNull,
    replaceUrl,
  } from '@mts-fe/utils';
  import { BasicHelp } from '@mts/components/Basic';
  import { MIcon } from '@mts/components/Icon';
  import { t } from '@mts/hooks/web/useI18n';
  import { usePermission } from '@mts/hooks/web/usePermission';
  import { getMappingData, inMtsApp, warn } from '@mts/utils';
  import { IN_DEV } from '@mts/utils/env';
  import {
    createPlaceholderMessage,
    isIncludeSimpleComponents,
    NO_AUTO_LINK_COMPONENTS,
    RANGE_TYPE,
    setComponentRuleType,
  } from '../helper';
  import { useItemLabelWidth } from '../hooks/useLabelWidth';
  import { componentMap } from '../simpleComponentMap';
  import {
    type FormActionType,
    type FormProps,
    type FormSchemaInner as FormSchema,
    isComponentFormSchema,
  } from '../types/form';

  export default defineComponent({
    name: 'SimpleFormItem',
    inheritAttrs: false,
    props: {
      schema: {
        type: Object as PropType<FormSchema>,
        default: () => ({}),
      },
      formProps: {
        type: Object as PropType<FormProps>,
        default: () => ({}),
      },
      allDefaultValues: {
        type: Object as PropType<Recordable<any>>,
        default: () => ({}),
      },
      formModel: {
        type: Object as PropType<Recordable<any>>,
        default: () => ({}),
      },
      setFormModel: {
        type: Function as PropType<(key: string, value: any, schema: FormSchema) => void>,
        default: null,
      },
      togglePin: {
        type: Function as PropType<(key: string, value: boolean, schema: FormSchema) => void>,
        default: null,
      },
      formActionType: {
        type: Object as PropType<FormActionType>,
      },
      canFocusFixed: Boolean,
      noRules: Boolean,
    },
    setup(props, { slots }) {
      const { schema, formProps } = toRefs(props) as {
        schema: Ref<FormSchema>;
        formProps: Ref<FormProps>;
      };

      const itemLabelWidthProp = useItemLabelWidth(schema, formProps);

      const { hasPermission } = usePermission();
      const getValues = computed(() => {
        const { allDefaultValues, formModel, schema } = props;
        return {
          field: schema.field,
          model: formModel,
          values: {
            ...allDefaultValues,
            ...formModel,
          },
          schema: schema,
          hasPermission,
        };
      });

      const getComponentsProps = computed(() => {
        const { schema, formModel, formActionType } = props;
        let { componentProps = {} } = schema;
        if (isFunction(componentProps)) {
          componentProps = componentProps({ schema, formModel, formActionType }) ?? {};
        }
        if (schema.fieldmapping?.length) {
          componentProps.onChange = (_, option) => {
            option && formActionType?.setFieldsValue(getMappingData(schema.fieldmapping!, option));
          };
        }
        if (schema.component === 'DatePicker') {
          let format;
          if (componentProps.picker === 'date' || isNil(componentProps.picker)) {
            format = DAY_FORMAT.D;
          }
          if (componentProps.picker === 'month') {
            format = DAY_FORMAT.M;
          }
          if (format) {
            componentProps = Object.assign({ format, valueFormat: format }, componentProps);
          }
        }
        if (isIncludeSimpleComponents(schema.component)) {
          componentProps = Object.assign(
            { type: 'horizontal' },
            {
              orientation: 'left',
              plain: true,
            },
            componentProps,
          );
        }
        componentProps.options?.forEach((i) => {
          i.label = typeof i.label === 'string' ? t(i.label) : i.label;
        });
        schema['_key'] &&
          componentProps.params.search &&
          Object.assign(componentProps.params.search, { _t: schema['_key'] });
        return cloneDeep(componentProps) as Recordable<any>;
      });

      const getDisable = computed(() => {
        const { disabled: globDisabled } = props.formProps;
        const { dynamicDisabled } = props.schema;
        const { disabled: itemDisabled = false } = unref(getComponentsProps);
        let disabled = !!globDisabled || itemDisabled;
        if (isBoolean(dynamicDisabled)) {
          disabled = dynamicDisabled;
        }
        if (isFunction(dynamicDisabled)) {
          disabled = dynamicDisabled(unref(getValues));
        }
        return disabled;
      });

      const getReadonly = computed(() => {
        const { readonly: globReadonly } = props.formProps;
        const { dynamicReadonly } = props.schema;
        const { readonly: itemReadonly = false } = unref(getComponentsProps);

        let readonly = globReadonly || itemReadonly;
        if (isBoolean(dynamicReadonly)) {
          readonly = dynamicReadonly;
        }
        if (isFunction(dynamicReadonly)) {
          readonly = dynamicReadonly(unref(getValues));
        }
        return readonly;
      });

      function getShow(): { isShow: boolean; isIfShow: boolean } {
        const { show, ifShow } = props.schema;
        let isShow = true;
        let isIfShow = true;

        if (isBoolean(show)) {
          isShow = show;
        }
        if (isBoolean(ifShow)) {
          isIfShow = ifShow;
        }
        if (isFunction(show)) {
          isShow = show(unref(getValues));
        }
        if (isFunction(ifShow)) {
          isIfShow = ifShow(unref(getValues));
        }
        return { isShow, isIfShow };
      }
      function handleRules(): ValidationRule[] {
        const {
          rules: defRules = [],
          component,
          rulesMessageJoinLabel = true,
          label,
          dynamicRules,
          required,
        } = props.schema;
        if (isFunction(dynamicRules)) {
          return dynamicRules(unref(getValues)) as ValidationRule[];
        }

        let rules = cloneDeep(defRules) as ValidationRule[];

        const assertLabel = rulesMessageJoinLabel ? (isFunction(label) ? '' : label) : '';
        const defaultMsg = component
          ? (createPlaceholderMessage(component) || '') + assertLabel
          : assertLabel;

        function validator(rule: any, value: any) {
          const msg = rule.message || defaultMsg;
          if (value === undefined || isNull(value) || (component === 'Stepper' && value === 0)) {
            // 空值
            return Promise.reject(msg);
          } else if (isArray(value) && value.length === 0) {
            // 数组类型
            return Promise.reject(msg);
          } else if (typeof value === 'string' && value.trim() === '') {
            // 空字符串
            return Promise.reject(msg);
          } else if (
            typeof value === 'object' &&
            Reflect.has(value, 'checked') &&
            Reflect.has(value, 'halfChecked') &&
            isArray(value.checked) &&
            isArray(value.halfChecked) &&
            value.checked.length === 0 &&
            value.halfChecked.length === 0
          ) {
            // 非关联选择的tree组件
            return Promise.reject(msg);
          }
          return Promise.resolve();
        }
        const getRequired = isFunction(required) ? required(unref(getValues)) : required;

        /*
         * 1、若设置了required属性，又没有其他的rules，就创建一个验证规则；
         * 2、若设置了required属性，又存在其他的rules，则只rules中不存在required属性时，才添加验证required的规则
         *     也就是说rules中的required，优先级大于required
         */
        if (getRequired) {
          if (!rules || rules.length === 0) {
            const trigger = NO_AUTO_LINK_COMPONENTS.includes(component || 'Input')
              ? 'blur'
              : 'change';
            rules = [{ required: getRequired, validator, trigger }];
          } else {
            const requiredIndex: number = rules.findIndex((rule) => Reflect.has(rule, 'required'));

            if (requiredIndex === -1) {
              rules.push({ required: getRequired, validator });
            }
          }
        }

        const requiredRuleIndex: number = rules.findIndex(
          (rule) => Reflect.has(rule, 'required') && !Reflect.has(rule, 'validator'),
        );

        if (requiredRuleIndex !== -1) {
          const rule = rules[requiredRuleIndex];
          const { isShow } = getShow();
          if (!isShow) {
            rule.required = false;
          }
          if (component) {
            rule.message = rule.message || defaultMsg;

            if (component.includes('Input') || component.includes('Textarea')) {
              rule.whitespace = true;
            }
            const valueFormat = unref(getComponentsProps)?.valueFormat;
            setComponentRuleType(rule, component, valueFormat);
          }
        }

        // Maximum input length rule check
        const characterInx = rules.findIndex((val) => val.max);
        if (characterInx !== -1 && !rules[characterInx].validator) {
          rules[characterInx].message =
            rules[characterInx].message || t('component.form.maxTip', [rules[characterInx].max]);
        }
        return rules;
      }

      function renderComponent() {
        if (!isComponentFormSchema(props.schema)) {
          return null;
        }
        const {
          renderComponentContent,
          component,
          field,
          changeEvent = 'change',
          valueField,
          valueFormat,
          label,
          hideLabel,
        } = props.schema;

        const isCheck = component && ['Switch', 'Checkbox'].includes(component);

        const eventKey = `on${upperFirst(changeEvent)}`;

        const on = {
          [eventKey]: (...args: Nullable<Recordable<any>>[]) => {
            const [e] = args;

            const target = e ? e.target : null;
            let value = target ? (isCheck ? target.checked : target.value) : e;
            if (isFunction(valueFormat)) {
              value = valueFormat({ ...unref(getValues), value });
            }
            props.setFormModel(field, value, props.schema);

            if (propsData[eventKey]) {
              propsData[eventKey](...args);
            }
          },
        };
        const Comp = componentMap.get(component) as ReturnType<typeof defineComponent>;
        !Comp && warn(`【${component}】不支持在SimpleForm中使用`);

        const { autoSetPlaceHolder, size } = props.formProps;
        const propsData: Recordable<any> = {
          allowClear: true,
          size,
          ...unref(getComponentsProps),
          disabled: unref(getDisable),
          readonly: unref(getReadonly),
        };
        if (inMtsApp && (propsData.focusFixed || props.canFocusFixed)) {
          if (component === 'ApiSelect') {
            propsData.focusFixed = true;
          }
          if (component === 'Input') {
            propsData.class = (propsData.class || '') + 'mts-input-focused-fixed';
          }
        }

        const isCreatePlaceholder = !propsData.disabled && autoSetPlaceHolder;
        // RangePicker place is an array
        if (isCreatePlaceholder && !RANGE_TYPE.includes(component) && component) {
          propsData.placeholder =
            t(unref(getComponentsProps)?.placeholder) ||
            (hideLabel && (isFunction(label) ? label(unref(getValues)) : label)) ||
            createPlaceholderMessage(component);
        }
        propsData.codeField = field;
        propsData.formValues = unref(getValues);

        const bindValue: Recordable<any> = {
          [valueField || (isCheck ? 'checked' : 'value')]: props.formModel[field],
        };

        const compAttr: Recordable<any> = {
          ...propsData,
          ...on,
          ...bindValue,
        };

        ((attrs: string[], values = compAttr.formValues.values) => {
          attrs.forEach((key) => {
            if (compAttr[key] && typeof compAttr[key] === 'string') {
              const bakKey = '_' + key;
              if (!compAttr[bakKey]) {
                compAttr[bakKey] = compAttr[key];
              }
              compAttr[key] = replaceUrl(compAttr[bakKey], values);
            }
          });
        })(['prefix', 'suffix', 'suffixIcon', 'addonBefore', 'addonAfter']);

        if (!renderComponentContent) {
          return <Comp {...compAttr} />;
        }
        const compSlot = isFunction(renderComponentContent)
          ? {
              ...renderComponentContent(unref(getValues), {
                disabled: unref(getDisable),
                readonly: unref(getReadonly),
              }),
            }
          : {
              default: () => renderComponentContent,
            };
        return <Comp {...compAttr}>{compSlot}</Comp>;
      }

      function renderLabelHelpMessage() {
        const {
          field,
          hideInMore,
          disablePin,
          label,
          helpMessage,
          helpComponentProps,
          subLabel,
          hideLabel,
          dataLangKey,
        } = props.schema;
        const getLabel = isFunction(label) ? label(unref(getValues)) : label;
        const renderSubLabel = subLabel ? <span class="text-secondary">{subLabel}</span> : void 0;

        if ((!label && !renderSubLabel) || hideLabel) return;

        const getHelpMessage = isFunction(helpMessage)
          ? helpMessage(unref(getValues))
          : helpMessage;
        const renderHelp =
          !getHelpMessage || (isArray(getHelpMessage) && getHelpMessage.length === 0) ? (
            void 0
          ) : (
            <BasicHelp placement="top" class="mx-1" text={getHelpMessage} {...helpComponentProps} />
          );

        return (
          <Fragment>
            {props.formProps.labelEllipsis ? (
              <Tooltip title={typeof getLabel === 'string' ? getLabel : ''} placement="topLeft">
                <span class="mts-label-ellipsis" data-lang-key={dataLangKey} v-langTips>
                  {getLabel}
                  {renderSubLabel}
                </span>
              </Tooltip>
            ) : (
              <span data-lang-key={dataLangKey} v-langTips>
                {getLabel}
                {renderSubLabel}
              </span>
            )}
            {renderHelp}

            {props.togglePin ? (
              <MIcon
                prefix="g"
                icon={hideInMore ? 'pin-outline' : 'pin-filled'}
                size={18}
                color={hideInMore ? '' : '#01A887'}
                class={
                  'mts-search-pin hidden ' +
                  (disablePin ? '!cursor-not-allowed !text-[#828282]' : '')
                }
                onClick={(e) => {
                  if (disablePin) return;
                  e.preventDefault();
                  e.stopPropagation();
                  props.togglePin(field, !hideInMore, props.schema);
                }}
              />
            ) : null}
          </Fragment>
        );
      }

      function renderItem() {
        const { itemProps, slot, render, field, suffix, component, prefix } = props.schema;
        const { labelCol, wrapperCol } = unref(itemLabelWidthProp);
        const { colon } = props.formProps;
        const opts = { disabled: unref(getDisable), readonly: unref(getReadonly) };

        const getContent = () => {
          return slot
            ? getSlot(slots, slot, unref(getValues), opts)
            : render
              ? render(unref(getValues), opts)
              : renderComponent();
        };

        const showSuffix = !!suffix;
        const getSuffix = isFunction(suffix) ? suffix(unref(getValues)) : suffix;

        const showPrefix = !!prefix;
        const getPrefix = isFunction(prefix) ? prefix(unref(getValues)) : prefix;
        // TODO 自定义组件验证会出现问题，因此这里框架默认将自定义组件设置手动触发验证，如果其他组件还有此问题请手动设置autoLink=false
        if (component && NO_AUTO_LINK_COMPONENTS.includes(component)) {
          props.schema &&
            (props.schema.itemProps! = {
              autoLink: false,
              ...props.schema.itemProps,
            });
        }

        return (
          <Form.Item
            data-field={props.schema.field}
            name={field}
            colon={colon}
            class={{
              'suffix-item': showSuffix,
              'prefix-item': showPrefix,
            }}
            {...(itemProps as Recordable<any>)}
            label={renderLabelHelpMessage()}
            rules={!props.noRules ? handleRules() : void 0}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
          >
            <div class="flex">
              {showPrefix && <span class="prefix">{getPrefix}</span>}
              <div style="flex:1; max-width:100%;">{getContent()}</div>
              {showSuffix && <span class="suffix">{getSuffix}</span>}
            </div>
          </Form.Item>
        );
      }

      return () => {
        const { colProps = {}, colSlot, renderColContent, component, slot } = props.schema;
        if (!component && !slot) {
          if (props.schema.field === '_BR_') {
            const { showSplit } = props.schema.componentProps;
            IN_DEV && showSplit && console.error('表单内置分割线已废弃');
            return (
              <Col class={`mx-6px w-full bg-[#EBEDF0] ${showSplit ? 'mb-8px' : '!min-h-0'}`}></Col>
            );
          }
          return null;
        }

        const { baseColProps = {} } = props.formProps;
        const realColProps = { ...baseColProps, ...colProps };
        const { isIfShow, isShow } = getShow();
        const values = unref(getValues);
        const opts = { disabled: unref(getDisable), readonly: unref(getReadonly) };

        const getContent = () => {
          return colSlot
            ? getSlot(slots, colSlot, values, opts)
            : renderColContent
              ? renderColContent(values, opts)
              : renderItem();
        };

        return (
          hasPermission(props.schema.auth) &&
          isIfShow && (
            <Col {...realColProps} v-show={isShow}>
              {getContent()}
            </Col>
          )
        );
      };
    },
  });
</script>

<style lang="less">
  /* label 省略号截断样式（配合 Tooltip 使用） */
  .mts-label-ellipsis {
    display: inline-block;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }
</style>
